/**
 * Cookie / tracker cleanup utilities — used when the user refuses cookies or
 * revokes their consent. We can only act on first-party cookies (those set
 * for the current document.cookie scope); third-party cookies set by external
 * scripts (e.g. PostHog us.i.posthog.com) are deleted via their own SDK
 * methods (posthog.opt_out_capturing() additionally wipes its own storage).
 *
 * The single key we MUST preserve is the consent decision itself
 * (`fluo_cookie_consent`), classified as "strictly necessary" under CNIL
 * guidance — otherwise we would re-prompt the user forever.
 */

const KEEP_COOKIES = new Set([]);           // none — see explanation above
const KEEP_LOCAL_KEYS = new Set([
  'fluo_cookie_consent',                    // the decision itself
  'i18nextLng',                             // some i18next builds use cookies; localStorage variant
]);

/** Expire a single cookie by name. We attempt deletion across the few likely
 *  path/domain combinations because the browser only deletes a cookie when
 *  the call matches both its original path and domain. */
const deleteCookie = (name) => {
  const host = window.location.hostname;
  // Build a list of (path, domain) tuples to try.
  const paths = ['/', window.location.pathname];
  const hostParts = host.split('.');
  const domains = ['', host];
  // Cover .example.com, .sub.example.com, etc.
  for (let i = 1; i < hostParts.length; i++) {
    domains.push('.' + hostParts.slice(i - 1).join('.'));
  }
  for (const path of paths) {
    for (const domain of domains) {
      const dom = domain ? `; domain=${domain}` : '';
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}${dom}; SameSite=Lax`;
    }
  }
};

/** Wipe every first-party cookie that is not strictly necessary. */
export const clearAllCookies = () => {
  if (typeof document === 'undefined') return;
  const cookies = document.cookie ? document.cookie.split(';') : [];
  for (const raw of cookies) {
    const name = raw.split('=')[0].trim();
    if (!name) continue;
    if (KEEP_COOKIES.has(name)) continue;
    deleteCookie(name);
  }
};

/** Drop every localStorage entry that is not the consent decision. */
export const clearPreferenceStorage = () => {
  try {
    const keys = Object.keys(localStorage);
    for (const k of keys) {
      if (KEEP_LOCAL_KEYS.has(k)) continue;
      localStorage.removeItem(k);
    }
  } catch { /* private mode / quota */ }
  try {
    sessionStorage.clear();
  } catch { /* noop */ }
};

/** Tell PostHog (if loaded) to stop capturing and forget anything it knows
 *  about the visitor — clears the ph_* cookies the SDK creates. */
export const stopPostHogTracking = () => {
  try {
    if (typeof window === 'undefined') return;
    if (window.posthog && typeof window.posthog.opt_out_capturing === 'function') {
      window.posthog.opt_out_capturing();
    }
    if (window.posthog && typeof window.posthog.reset === 'function') {
      window.posthog.reset(true); // true = also clear distinct_id
    }
  } catch { /* SDK not ready yet — call again on next page load */ }
};

/** Inverse: let PostHog start capturing after the user accepts. */
export const startPostHogTracking = () => {
  try {
    if (typeof window === 'undefined') return;
    if (window.posthog && typeof window.posthog.opt_in_capturing === 'function') {
      window.posthog.opt_in_capturing();
    }
  } catch { /* noop */ }
};

/** Full cleanup invoked from `useConsent` when the user refuses or revokes. */
export const fullRefuseCleanup = () => {
  stopPostHogTracking();
  clearAllCookies();
  clearPreferenceStorage();
};
